import { bucketize, bucketizeCounts, type BucketEntry } from './bucketize';
import { groupBarSegmentColor } from './signalColors';
import type { SidebarPalette } from './sidebarPalettes';

import type { ExplorationVolume } from '../../types/exploration';

export interface GroupBarSegment {
  key: string;
  pct: number;
  color: string;
}

export interface BreakdownSegment {
  key: string;
  color: string;
  label: string;
  count: number;
  size: number;
  pct: number;
  flexPct: number;
}

export interface BreakdownData {
  segments: BreakdownSegment[];
  legendRows: BreakdownSegment[];
  total: number;
}

function buildFromBuckets(
  buckets: Map<string, BucketEntry>,
  labelFn: (key: string) => string,
  colorFn: (key: string) => string
): BreakdownData {
  const total = [...buckets.values()].reduce((s, e) => s + (e.size || 0), 0);
  const sortedKeys = [...buckets.keys()]
    .filter((k) => (buckets.get(k)?.size || 0) > 0)
    .sort((a, b) => (buckets.get(b)?.size || 0) - (buckets.get(a)?.size || 0));

  const segments: BreakdownSegment[] = sortedKeys.map((key) => {
    const e = buckets.get(key)!;
    const pct = total > 0 ? Math.round((e.size / total) * 100) : 0;
    return {
      key,
      color: colorFn(key),
      label: labelFn(key),
      count: e.count,
      size: e.size,
      pct,
      flexPct: total > 0 ? (e.size / total) * 100 : 0,
    };
  });

  return {
    segments,
    legendRows: segments,
    total,
  };
}

export function buildVolumeBreakdownData(
  volumes: ExplorationVolume[],
  field: 'activationStatus' | 'supportStatus',
  order: string[],
  labelFn: (key: string) => string,
  colorFn: (key: string) => string
): BreakdownData {
  const buckets = bucketize(volumes, field, order as [string, ...string[]]);
  return buildFromBuckets(buckets as Map<string, BucketEntry>, labelFn, colorFn);
}

export function buildActivationGroupBarSegments(
  volumes: ExplorationVolume[],
  palette: SidebarPalette
): GroupBarSegment[] | undefined {
  const breakdown = buildVolumeBreakdownData(
    volumes,
    palette.field,
    palette.order,
    palette.label,
    palette.color
  );
  const byKey = new Map(breakdown.segments.map((segment) => [segment.key, segment]));
  const segments = palette.order
    .map((key) => byKey.get(key))
    .filter((segment): segment is BreakdownSegment => segment != null && segment.size > 0)
    .map((segment) => ({
      key: segment.key,
      pct: segment.flexPct,
      color: groupBarSegmentColor(segment.key, segment.color),
    }));

  return segments.length > 0 ? segments : undefined;
}

export function buildCountBreakdownData<T extends string>(
  counts: Partial<Record<T, number>>,
  order: T[],
  labelFn: (key: T) => string,
  colorFn: (key: T) => string,
  sizeWeights?: Partial<Record<T, number>>
): BreakdownData {
  const buckets = bucketizeCounts(counts, order, sizeWeights);
  return buildFromBuckets(
    buckets as Map<string, BucketEntry>,
    (k) => labelFn(k as T),
    (k) => colorFn(k as T)
  );
}
